import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';
import { useCredentialsState } from '../../contexts';
import { NewCredentialModal } from '../NewCredentialModal';
import type { Credential } from '../../api/Credentials/types';

interface CredentialSelectorProps {
  value: string;
  onChange: (value: string) => void;
  onError: (message: string) => void;
  placeholder?: string;
  className?: string;
}

export const CredentialSelector: React.FC<CredentialSelectorProps> = ({
  value,
  onChange,
  onError,
  placeholder = 'Select an credential',
  className = 'w-full',
}) => {
  const { credentials, fetchCredentials } = useCredentialsState();
  const [isNewAuthModalOpen, setIsNewAuthModalOpen] = useState(false);
  const [newlyCreatedAuth, setNewlyCreatedAuth] = useState<Credential | null>(null);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === '__new__') {
      setIsNewAuthModalOpen(true);
    } else {
      onChange(selectedValue);
    }
  };

  const handleCredentialCreated = (credential: Credential) => {
    setNewlyCreatedAuth(credential);
    onChange(credential.name);
  };

  return (
    <>
      <Select key={newlyCreatedAuth?.uid || 'auth-select'} value={value} onValueChange={handleValueChange}>
        <SelectTrigger className={`py-2.5 ${className}`} size="sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__new__" className="text-primary-600 font-medium">
            + New Credential
          </SelectItem>
          {newlyCreatedAuth && (
            <SelectItem key={newlyCreatedAuth.uid} value={newlyCreatedAuth.name}>
              {newlyCreatedAuth.displayName}
            </SelectItem>
          )}
          {credentials.map((auth) => (
            <SelectItem key={auth.uid} value={auth.name}>
              {auth.displayName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <NewCredentialModal
        isOpen={isNewAuthModalOpen}
        onClose={() => setIsNewAuthModalOpen(false)}
        onCreated={handleCredentialCreated}
        onError={onError}
      />
    </>
  );
};
